/**
 * Shared scroll engine.
 *
 * A single passive scroll + resize listener for the whole app, batched into one
 * requestAnimationFrame tick. Components/hooks subscribe with a callback that
 * reads layout (window.scrollY / getBoundingClientRect) and updates their own
 * state. This replaces the previous pattern of every component registering its
 * own `window.addEventListener("scroll", ...)`, which meant N listeners all
 * firing on the same frame.
 */

type ScrollCallback = () => void;

const subscribers = new Set<ScrollCallback>();
let ticking = false;
let listening = false;

function flush() {
  ticking = false;
  // Snapshot to avoid mutation-during-iteration issues.
  for (const cb of Array.from(subscribers)) {
    try {
      cb();
    } catch {
      /* a single broken subscriber must not break the rest */
    }
  }
}

function onEvent() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(flush);
}

function ensureListening() {
  if (listening || typeof window === "undefined") return;
  listening = true;
  window.addEventListener("scroll", onEvent, { passive: true });
  window.addEventListener("resize", onEvent, { passive: true });
}

/**
 * Subscribe to the shared scroll loop. The callback fires once immediately and
 * then on every batched scroll/resize frame. Returns an unsubscribe function.
 */
export function subscribeScroll(cb: ScrollCallback): () => void {
  ensureListening();
  subscribers.add(cb);
  cb(); // initial sync
  return () => {
    subscribers.delete(cb);
  };
}

/** True when the user has requested reduced motion. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}
